import {Partytown} from '@qwik.dev/partytown/react'
import {PersistQueryClientProvider} from '@tanstack/react-query-persist-client'
import {createRootRouteWithContext, HeadContent} from '@tanstack/react-router'
import {Provider as JotaiProvider} from 'jotai'
import {RouterProvider as RACRouterProvider} from 'react-aria-components'
import {ErrorBoundary} from 'react-error-boundary'

import {DEBUG, ENABLE_DEVTOOLS} from '@/constants/config'
import Global from '@/Global'
import Head from '@/lib/head/Head'
import {createQueryPersistOptions} from '@/queries/queries'
import type {RouterContext} from '@/router'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {logError} from '@/utils/logger'
import ErrorComponent from '@/views/Error/ErrorComponent'
import QueryErrorComponent from '@/views/Error/QueryErrorComponent'

const JotaiDevTools = ENABLE_DEVTOOLS
  ? lazy(async () => import('@/utils/components/JotaiDevTools'))
  : () => null

const Inspector = ENABLE_DEVTOOLS
  ? lazy(async () => import('react-dev-inspector').then(res => ({default: res.Inspector})))
  : () => null

const ReactQueryDevtools = ENABLE_DEVTOOLS
  ? lazy(async () =>
      import('@tanstack/react-query-devtools').then(res => ({default: res.ReactQueryDevtools})),
    )
  : () => null

const TanStackRouterDevtools = ENABLE_DEVTOOLS
  ? lazy(async () =>
      import('@tanstack/router-devtools').then(res => ({
        default: res.TanStackRouterDevtools,
        // For Embedded Mode
        // default: res.TanStackRouterDevtoolsPanel
      })),
    )
  : () => null

const QueryErrorBoundary = deepMemo(function QueryErrorBoundary({children}: PropsWithChildren) {
  const {reset} = useQueryErrorResetBoundary()

  return (
    <ErrorBoundary onReset={reset} fallbackRender={QueryErrorComponent}>
      {children}
    </ErrorBoundary>
  )
})

const DevTool = deepMemo(function DevTool({children}: PropsWithChildren) {
  if (!ENABLE_DEVTOOLS) return null

  return (
    <ErrorBoundary fallback={null}>
      <Suspense>{children}</Suspense>
    </ErrorBoundary>
  )
})

function RootRoute() {
  const router = useRouter()
  const {store, queryClient} = useRouteContext({
    strict: false,
  })

  invariant(queryClient, 'queryClient is required')
  invariant(store, 'store is required')

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- not needed
  const [persistOptions] = useState(() => createQueryPersistOptions())
  const navigate = useCallback(async (to: string) => router.navigate({to}), [router])

  return (
    <>
      <ErrorBoundary fallback={null}>
        <Partytown debug={DEBUG} forward={['dataLayer.push']} />
      </ErrorBoundary>
      <ErrorBoundary
        fallbackRender={({error}) => {
          logError(error)
          return <ErrorComponent />
        }}
      >
        <JotaiProvider store={store}>
          <Head />
          <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
            <QueryErrorBoundary>
              <RACRouterProvider navigate={navigate}>
                <Global />
                <VisuallyHidden strict {...skipTargetProps('top')} />
                <HeadContent />
                <Outlet />
                <DevTool>
                  <Inspector />
                </DevTool>
              </RACRouterProvider>
            </QueryErrorBoundary>
            <DevTool>
              <ReactQueryDevtools initialIsOpen={false} />
            </DevTool>
          </PersistQueryClientProvider>
          <DevTool>
            <JotaiDevTools />
          </DevTool>
        </JotaiProvider>
      </ErrorBoundary>
      <DevTool>
        <TanStackRouterDevtools initialIsOpen={false} />
      </DevTool>
    </>
  )
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootRoute,
})
